import React__default, { useRef, useEffect } from 'react';
import { SizeEnum } from '@teko/types/ds';
import styled from '@emotion/styled';
import { CommonStyles } from '../BaseProps/index.es.js';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const InputSlider = (_a) => {
  var _b = _a, {
    range,
    min = 0,
    max = 100,
    value,
    defaultValue,
    onChange,
    disabled,
    step = 1,
    ref,
    tipFormatter,
    size = SizeEnum.m,
    thumbSize,
    color = "primary",
    tooltip = true,
    changeOn = "dragging"
  } = _b, props = __objRest(_b, [
    "range",
    "min",
    "max",
    "value",
    "defaultValue",
    "onChange",
    "disabled",
    "step",
    "ref",
    "tipFormatter",
    "size",
    "thumbSize",
    "color",
    "tooltip",
    "changeOn"
  ]);
  var _a2, _b2, _c;
  const valueDefault = defaultValue ? defaultValue : range ? [min, max] : min;
  const valueSlider = value !== void 0 ? value : valueDefault;
  const resolvedThumb = thumbSize != null ? thumbSize : sizes[size].thumb;
  const innerRef = useRef(null);
  const trackFillRef = useRef(null);
  const tooltipRefs = useRef([null, null]);
  const thumbRefs = useRef([null, null]);
  const mouseMoveEventRef = useRef(null);
  const mouseUpEventRef = useRef(null);
  const percents = useRef(getPercent(max, min, valueSlider));
  const currentPercent = useRef(null);
  const isDragging = useRef(false);
  const formatter = (value2) => {
    if (value2 === void 0)
      return "";
    if (tipFormatter)
      return tipFormatter(value2);
    return new Intl.NumberFormat("en-CA", {
      maximumFractionDigits: 10
    }).format(value2);
  };
  useEffect(() => () => {
    document.removeEventListener("mousemove", mouseMoveEventRef.current || (() => {
    }));
    document.removeEventListener("mouseup", mouseUpEventRef.current || (() => {
    }));
    document.removeEventListener("touchmove", mouseMoveEventRef.current || (() => {
    }));
    document.removeEventListener("touchend", mouseUpEventRef.current || (() => {
    }));
  }, []);
  const clickInTrack = (e) => {
    var _a3;
    e.stopPropagation();
    const { pageX } = getPosition(e);
    const { left = 0, width } = ((_a3 = innerRef.current) == null ? void 0 : _a3.getBoundingClientRect()) || {};
    if (!width) {
      return;
    }
    const newValue = (Number(pageX || left) - left) / width * 100;
    const [currentLeft, currentRight] = percents.current;
    const [deltaLeft, deltaRight] = [
      newValue - currentLeft,
      currentRight - newValue
    ];
    const activeIndex = range && deltaLeft < deltaRight ? 0 : 1;
    setPositions(pageX, activeIndex);
    setTooltipVisible(activeIndex, "visible");
    changeValue();
  };
  const onStartMove = (e, index) => {
    e.stopPropagation();
    currentPercent.current = percents.current[index];
    const onMouseMove = (event) => {
      isDragging.current = true;
      const { pageX: moveX } = getPosition(event);
      setPositions(moveX, index);
    };
    const onMouseUp = () => {
      setTooltipVisible(index, "hidden");
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("touchend", onMouseUp);
      document.removeEventListener("touchmove", onMouseMove);
      changeOn === "dragend" && changeValue();
      mouseMoveEventRef.current = null;
      mouseUpEventRef.current = null;
      isDragging.current = false;
      currentPercent.current = null;
    };
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("touchend", onMouseUp);
    document.addEventListener("touchmove", onMouseMove);
    mouseMoveEventRef.current = onMouseMove;
    mouseUpEventRef.current = onMouseUp;
  };
  useEffect(() => {
    if (percents.current === void 0 || valueSlider === void 0)
      return;
    const [leftPercent, rightPercent] = getPercent(max, min, valueSlider);
    if (percents.current[0] === leftPercent && percents.current[1] === rightPercent) {
      return;
    }
    percents.current = [leftPercent, rightPercent];
    determinePosition(rightPercent - leftPercent, leftPercent);
  }, [valueSlider.toString(), max, min, range]);
  const getValue = (percent) => {
    const value2 = percent * (max - min) / 100;
    const v = Math.round(value2 / step) * step;
    const displayValue = Math.round(v * 100) / 100 + min;
    return displayValue > max ? max : displayValue;
  };
  const setPositions = (pageX = 0, index = null) => {
    var _a3;
    const { left = 0, width } = ((_a3 = innerRef.current) == null ? void 0 : _a3.getBoundingClientRect()) || {};
    if (!width) {
      return;
    }
    const newValue = (Number(pageX || left) - left) / width * 100;
    changePercent(newValue, index);
    changeOn === "dragging" && changeValue();
  };
  const determinePosition = (percent, leftPercent) => {
    if (trackFillRef.current) {
      trackFillRef.current.style.width = `${percent}%`;
      trackFillRef.current.style.left = `${leftPercent}%`;
    }
    if (thumbRefs.current && tooltipRefs.current) {
      percents.current.forEach((pos, index) => {
        if (thumbRefs.current[index]) {
          thumbRefs.current[index].style.left = `calc(${pos}% ${pos === 100 ? `- ${resolvedThumb / 2}px` : ""})`;
        }
        if (tooltipRefs.current[index]) {
          tooltipRefs.current[index].innerText = formatter(getValue(pos));
        }
      });
    }
  };
  const changeValue = () => {
    const [_left, _right] = percents.current;
    const newValues = [getValue(_left), getValue(_right)];
    if (newValues.toString() === valueSlider.toString())
      return;
    onChange == null ? void 0 : onChange(range ? newValues : newValues[1]);
  };
  const changePercent = (value2, index = null) => {
    let newValue = value2;
    const ltr = newValue - (currentPercent.current || 0) > 0;
    if (percents.current) {
      let [thumbLeft, thumbRight] = percents.current;
      if (ltr && getValue(newValue) > getValue(thumbRight) && index === 0) {
        return;
      }
      if (!ltr && getValue(newValue) < getValue(thumbLeft) && index === 1) {
        return;
      }
      if (newValue >= 100) {
        newValue = 100;
      }
      if (newValue <= 0) {
        newValue = 0;
      }
      if (index === null) {
        const middle = (thumbLeft + thumbRight) / 2;
        currentPercent.current = newValue;
        if (newValue > middle || !range) {
          thumbRight = newValue;
        } else {
          thumbLeft = newValue;
        }
        percents.current = [thumbLeft, thumbRight];
      } else {
        percents.current[index] = newValue;
      }
      const [_left, _right] = percents.current;
      const percent = _right - _left;
      determinePosition(percent, _left);
    }
  };
  const setTooltipVisible = (index, value2) => {
    if (tooltipRefs.current[index])
      tooltipRefs.current[index].style.visibility = value2;
  };
  return /* @__PURE__ */ React__default.createElement(SliderWrapper, __spreadValues({}, props), /* @__PURE__ */ React__default.createElement(Wrapper, {
    ref: innerRef,
    size,
    thumbSize: resolvedThumb,
    color,
    onMouseDown: clickInTrack
  }, /* @__PURE__ */ React__default.createElement(SliderTrack, {
    className: "slider-track"
  }, /* @__PURE__ */ React__default.createElement(SliderTrackFill, {
    className: "slider-track-fill",
    ref: trackFillRef,
    style: {
      width: `${((_a2 = percents.current) == null ? void 0 : _a2[1]) - ((_b2 = percents.current) == null ? void 0 : _b2[0])}%`,
      left: `${(_c = percents.current) == null ? void 0 : _c[0]}%`
    }
  })), [...Array(2)].map((_, index) => {
    var _a3, _b3;
    return !range && index == 0 ? null : /* @__PURE__ */ React__default.createElement(StylerThumb, {
      key: index,
      className: "slider-thumb",
      ref: (e) => {
        thumbRefs.current[index] = e;
      },
      style: {
        left: `calc(${(_a3 = percents.current) == null ? void 0 : _a3[index]}% ${((_b3 = percents.current) == null ? void 0 : _b3[index]) === 100 ? `- ${resolvedThumb / 2}px` : ""})`
      },
      onMouseDown: (e) => onStartMove(e, index),
      onTouchStart: (e) => onStartMove(e, index),
      onMouseMove: () => !isDragging.current && setTooltipVisible(index, "visible"),
      onTouchMove: () => !isDragging.current && setTooltipVisible(index, "visible"),
      onMouseOut: () => !isDragging.current && setTooltipVisible(index, "hidden"),
      onTouchCancel: () => !isDragging.current && setTooltipVisible(index, "hidden"),
      role: "slider"
    }, tooltip && /* @__PURE__ */ React__default.createElement(StyleTooltip, {
      className: "tooltip",
      ref: (e) => tooltipRefs.current[index] = e
    }, formatter(getValue(percents.current[index]))));
  })));
};
InputSlider.displayName = "InputSlider";
const SliderWrapper = styled.div`
  ${CommonStyles};
  padding: 0.5rem 0;
`;
const Wrapper = styled.div(({ theme, size, thumbSize, color }) => `
  position: relative;
  height: ${sizes[size || SizeEnum.m].height}px;
  width: 100%;

  & >.slider-track .slider-track-fill {
     background-color: ${theme.colors[color] || color};
  }

  & >.slider-thumb{
    box-shadow: 0px 0px 0px 1.5px ${theme.colors[color] || color} inset;
    width: ${thumbSize != null ? thumbSize : sizes[size || SizeEnum.m].thumb}px;
    height:  ${thumbSize != null ? thumbSize : sizes[size || SizeEnum.m].thumb}px;
  }
`);
const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background};
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
`;
const SliderTrackFill = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
`;
const StylerThumb = styled.div(({ theme }) => `
  position: absolute;
  border-radius: 1000px;
  top: 0;
  transform: translate(-25%, -50%);
  background-color: ${theme.colors.white};
  text-align: center;
  cursor: pointer;
  top: 50%
`);
const StyleTooltip = styled.span(({ theme }) => `
  visibility: hidden;
  background-color: ${theme.colors.black};
  color: #fff;
  text-align: center;
  padding: 2px 3px;
  position: absolute;
  z-index: 1;
  font-size: 0.625rem;
  border-radius: 3px;
  transform: translate(-50%, -150%);
  min-width: 1.25rem;
  user-select: none;
  // arrow
  &:after {
    content: '';
    width: 8px;
    height: 5px;
    bottom: -5px;
    border-top: 4px solid ${theme.colors.black};
    border-right: 4px solid transparent;
    border-left: 4px solid transparent;
    left: 50%;
    transform: translateX(-50%);
    position: absolute;
  }
`);
const sizes = {
  xs: {
    height: 3,
    thumb: 9
  },
  s: {
    height: 4,
    thumb: 11
  },
  m: { height: 5, thumb: 12 },
  l: { height: 7, thumb: 15 },
  xl: { height: 4, thumb: 20 }
};
function getPosition(e) {
  const obj = "touches" in e ? e.touches[0] : e;
  return { pageX: obj.pageX, pageY: obj.pageY };
}
const getPercent = (max, min, valueSlider) => {
  let leftValue = min, rightValue = max;
  if (typeof valueSlider === "number") {
    rightValue = valueSlider;
  } else {
    [leftValue, rightValue] = valueSlider;
  }
  let [leftPercent, rightPercent] = [
    (leftValue - min) / (max - min) * 100,
    (rightValue - min) / (max - min) * 100
  ];
  if (leftPercent < 0 || leftPercent == void 0)
    leftPercent = 0;
  if (rightPercent > 100 || rightPercent == void 0)
    rightPercent = 100;
  return [leftPercent, rightPercent];
};

export { InputSlider };
